// src/components/coordinateGrid/Measurement2DRenderer.tsx
/**
 * Measurement2DRenderer Component
 * 
 * Renders 2D measurements on the coordinate grid with Pythagorean distance.
 * Phase 6: Tool Integration
 */

import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../types/redux';
import { Point2D } from '../../types/coordinateGrid';
import { calculateDistance } from '../../lib/coordinateGrid/shapeCalculations';
import MathRenderer from '../MathRenderer';

interface Measurement2DRendererProps {
  /** Canvas dimensions */
  width: number;
  height: number;
  /** Viewport transform */
  scaleX: number;
  scaleY: number;
  offsetX: number;
  offsetY: number;
  /** Dark mode */
  isDark?: boolean;
}

/**
 * Convert math coordinates to screen coordinates
 */
function mathToScreen(
  point: Point2D,
  scaleX: number,
  scaleY: number,
  offsetX: number,
  offsetY: number
): { x: number; y: number } {
  return {
    x: offsetX + point.x * scaleX,
    y: offsetY - point.y * scaleY, // Y is inverted in screen coordinates
  };
}

export const Measurement2DRenderer: React.FC<Measurement2DRendererProps> = ({
  width,
  height,
  scaleX,
  scaleY,
  offsetX,
  offsetY,
  isDark = false,
}) => {
  const measurement = useSelector((state: RootState) => state.ui.measurement);

  // Render active measurement (being drawn)
  const activeMeasurementElement = useMemo(() => {
    if (!measurement.isActive || !measurement.startPoint || !measurement.endPoint) {
      return null;
    }

    // Get math coordinates from screen coordinates
    const startMath: Point2D = {
      x: (measurement.startPoint.x - offsetX) / scaleX,
      y: (offsetY - (measurement.startPoint.y || 0)) / scaleY,
    };
    
    const endMath: Point2D = {
      x: (measurement.endPoint.x - offsetX) / scaleX,
      y: (offsetY - (measurement.endPoint.y || 0)) / scaleY,
    };

    const startScreen = mathToScreen(startMath, scaleX, scaleY, offsetX, offsetY);
    const endScreen = mathToScreen(endMath, scaleX, scaleY, offsetX, offsetY);

    const distance = calculateDistance(startMath, endMath);
    const dx = endMath.x - startMath.x;
    const dy = endMath.y - startMath.y;

    // Calculate label position (midpoint, offset upward)
    const labelX = (startScreen.x + endScreen.x) / 2;
    const labelY = (startScreen.y + endScreen.y) / 2 - 30;

    return (
      <g key="active-measurement" className="active-measurement">
        {/* Measurement line */}
        <line
          x1={startScreen.x}
          y1={startScreen.y}
          x2={endScreen.x}
          y2={endScreen.y}
          stroke="#10B981"
          strokeWidth={2}
          strokeDasharray="5,5"
          opacity={0.7}
        />
        
        {/* Start point marker */}
        <circle
          cx={startScreen.x}
          cy={startScreen.y}
          r={4}
          fill="#10B981"
          opacity={0.7}
        />
        
        {/* End point marker */}
        <circle
          cx={endScreen.x}
          cy={endScreen.y}
          r={4}
          fill="#10B981"
          opacity={0.7}
        />
        
        {/* Distance label */}
        <text
          x={labelX}
          y={labelY}
          fill={isDark ? '#E5E7EB' : '#1F2937'}
          fontSize="12"
          fontWeight="600"
          textAnchor="middle"
        >
          {distance.toFixed(2)}
        </text>
      </g>
    );
  }, [measurement, scaleX, scaleY, offsetX, offsetY, isDark]);

  // Render completed measurements
  const completedMeasurementElements = useMemo(() => {
    return measurement.measurements
      .filter(m => m.point1 && m.point2)
      .map((meas) => {
        if (!meas.point1 || !meas.point2) return null;

        const startScreen = mathToScreen(meas.point1, scaleX, scaleY, offsetX, offsetY);
        const endScreen = mathToScreen(meas.point2, scaleX, scaleY, offsetX, offsetY);

        const distance = calculateDistance(meas.point1, meas.point2);
        const dx = meas.point2.x - meas.point1.x;
        const dy = meas.point2.y - meas.point1.y;

        const strokeColor = meas.selected ? '#F59E0B' : '#10B981';
        const strokeWidth = meas.selected ? 3 : 2;

        // Calculate label position
        const labelX = (startScreen.x + endScreen.x) / 2;
        const labelY = (startScreen.y + endScreen.y) / 2 - 30;

        // Build calculation string
        const dxStr = dx >= 0 ? `+${dx.toFixed(2)}` : dx.toFixed(2);
        const dyStr = dy >= 0 ? `+${dy.toFixed(2)}` : dy.toFixed(2);
        const calculation = `√((${dxStr})² + (${dyStr})²) = ${distance.toFixed(2)}`;

        return (
          <g key={meas.id} className={`measurement-${meas.id}`}>
            {/* Measurement line */}
            <line
              x1={startScreen.x}
              y1={startScreen.y}
              x2={endScreen.x}
              y2={endScreen.y}
              stroke={strokeColor}
              strokeWidth={strokeWidth}
            />

            {/* Component lines (if enabled) */}
            {meas.showComponents && (
              <>
                {/* Horizontal component (Δx) */}
                <line
                  x1={startScreen.x}
                  y1={startScreen.y}
                  x2={endScreen.x}
                  y2={startScreen.y}
                  stroke="#3B82F6"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                  opacity={0.5}
                />
                {/* Vertical component (Δy) */}
                <line
                  x1={endScreen.x}
                  y1={startScreen.y}
                  x2={endScreen.x}
                  y2={endScreen.y}
                  stroke="#EF4444"
                  strokeWidth={1}
                  strokeDasharray="3,3"
                  opacity={0.5}
                />
              </>
            )}

            {/* Start point marker */}
            <circle
              cx={startScreen.x}
              cy={startScreen.y}
              r={5}
              fill={strokeColor}
            />

            {/* End point marker */}
            <circle
              cx={endScreen.x}
              cy={endScreen.y}
              r={5}
              fill={strokeColor}
            />

            {/* Distance label */}
            <text
              x={labelX}
              y={labelY}
              fill={isDark ? '#E5E7EB' : '#1F2937'}
              fontSize="12"
              fontWeight="600"
              textAnchor="middle"
            >
              {meas.displayMode === 'exact' || meas.displayMode === 'both'
                ? distance.toFixed(2)
                : distance.toFixed(2)}
            </text>

            {/* Calculation (if enabled) */}
            {meas.showCalculation && (
              <foreignObject
                x={labelX - 100}
                y={labelY + 20}
                width="200"
                height="40"
              >
                <div
                  style={{
                    textAlign: 'center',
                    fontSize: '10px',
                    color: isDark ? '#9CA3AF' : '#6B7280',
                  }}
                >
                  <MathRenderer latex={calculation} />
                </div>
              </foreignObject>
            )}

            {/* Component labels (if enabled) */}
            {meas.showComponents && (
              <>
                <text
                  x={(startScreen.x + endScreen.x) / 2}
                  y={startScreen.y - 15}
                  fill="#3B82F6"
                  fontSize="10"
                  textAnchor="middle"
                >
                  Δx = {dx.toFixed(2)}
                </text>
                <text
                  x={endScreen.x + 15}
                  y={(startScreen.y + endScreen.y) / 2}
                  fill="#EF4444"
                  fontSize="10"
                >
                  Δy = {dy.toFixed(2)}
                </text>
              </>
            )}
          </g>
        );
      });
  }, [measurement.measurements, scaleX, scaleY, offsetX, offsetY, isDark]);

  return (
    <g className="measurement-2d-layer">
      {activeMeasurementElement}
      {completedMeasurementElements}
    </g>
  );
};


